import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getProfile, updateProfile } from '../services/profileService';
import apiClient from '../services/api';
import { getMemberLabData } from '../services/memberLabService';
import ProfileImageUpload from '../Components/ProfileImageUpload';
import MainHeader from '../Components/MainHeader';
import CustomAlert from '../Components/CustomAlert';
import { useCustomAlert } from '../hooks/useCustomAlert';

const ProfileContainer = styled.div`
 min-height: 100vh;
 background-color: white;
 font-family: 'Noto Sans Thai', sans-serif !important;
 * {
   font-family: 'Noto Sans Thai', sans-serif !important;
 }
`;


const Content = styled.div`
 padding: 24px;
 max-width: 400px;
 margin: 0 auto;
`;

const ProfileSection = styled.div`
 display: flex;
 flex-direction: column;
 align-items: center;
 margin-bottom: 32px;
`;

const ProfileImageContainer = styled.div`
 position: relative;
 margin-bottom: 24px;
`;

const ProfileImage = styled.div`
 width: 120px;
 height: 120px;
 border-radius: 50%;
 background-color: #d1d5db;
 display: flex;
 align-items: center;
 justify-content: center;
 position: relative;
`;

const ProfileIcon = styled.div`
 width: 60px;
 height: 60px;
 background-image: url("data:image/svg+xml,%3csvg width='60' height='60' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' fill='%23ffffff'/%3e%3c/svg%3e");
 background-repeat: no-repeat;
 background-position: center;
 background-size: contain;
`;

const EditButton = styled.div`
 position: absolute;
 bottom: 0;
 right: 0;
 width: 36px;
 height: 36px;
 border-radius: 50%;
 background-color: #00BBD3;
 display: flex;
 align-items: center;
 justify-content: center;
 cursor: pointer;
 transition: background-color 0.3s ease;
 
 &:hover {
   background-color: #009bb3;
 }
`;

const EditIcon = styled.div`
 width: 20px;
 height: 20px;
 background-image: url("data:image/svg+xml,%3csvg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z' fill='white'/%3e%3c/svg%3e");
 background-repeat: no-repeat;
 background-position: center;
 background-size: contain;
`;

const SectionTitle = styled.h2`
 font-size: 20px;
 font-weight: 600;
 color: #00BBD3;
 margin-bottom: 24px;
 font-family: 'Noto Sans Thai', sans-serif !important;
`;

const InfoGrid = styled.div`
 display: grid;
 gap: 16px;
 margin-bottom: 32px;
`;

const InfoRow = styled.div`
 display: flex;
 justify-content: space-between;
 align-items: center;
 padding: 12px 0;
 border-bottom: 1px solid #e1e5e9;
`;

const InfoLabel = styled.span`
 font-size: 16px;
 color: #666666;
 font-weight: bold;
 font-family: 'Noto Sans Thai', sans-serif !important;
`;

const InfoValue = styled.input`
 font-size: 16px;
 color: #666;
 font-weight: bold;
 font-family: 'Noto Sans Thai', sans-serif !important;
 border: none;
 background: transparent;
 text-align: right;
 width: 200px;
 
 &:focus {
   outline: none;
   background-color: #f8f9fa;
   border-radius: 4px;
   padding: 4px 8px;
 }
`;

const FormSection = styled.div`
 margin-bottom: 32px;
`;

const FormRow = styled.div`
 display: flex;
 align-items: center;
 justify-content: space-between;
 margin-bottom: 20px;
 gap: 16px;
 width: 100%;
`;

const FormLabel = styled.label`
 font-size: 16px;
 color: #666666;
 font-weight: bold;
 font-family: 'Noto Sans Thai', sans-serif !important;
 flex: 1;
 text-align: left;
`;

const FormInputContainer = styled.div`
 display: flex;
 align-items: center;
 justify-content: center;
 gap: 8px;
 flex: 2;
`;

const SmallInput = styled.input`
 width: 80px;
 padding: 12px 16px;
 border: 2px solid #e1e5e9;
 border-radius: 12px;
 font-size: 16px;
 color: #666666;
 background-color: white;
 transition: border-color 0.3s ease;
 font-family: 'Noto Sans Thai', sans-serif !important;
 font-weight: bold;
 text-align: center;

 &:focus {
   outline: none;
   border-color: #00BBD3;
 }
`;

const UnitLabel = styled.span`
 font-size: 16px;
 color: #666;
 font-weight: bold;
 font-family: 'Noto Sans Thai', sans-serif !important;
 flex: 1;
 text-align: right;
`;

const DiseaseSection = styled.div`
 margin-top: 40px;
 margin-bottom: 32px;
`;

const DiseaseTitle = styled.h2`
 font-size: 20px;
 font-weight: 600;
 color: #00BBD3;
 margin-bottom: 16px;
 font-family: 'Noto Sans Thai', sans-serif !important;
`;

const DiseaseDescription = styled.p`
 font-size: 14px;
 color: #666;
 line-height: 1.5;
 margin-bottom: 24px;
 font-weight: bold;
 font-family: 'Noto Sans Thai', sans-serif !important;
`;

const RadioRow = styled.div`
 display: flex;
 align-items: center;
 gap: 24px;
 margin-bottom: 20px;
`;

const RadioGroup = styled.div`
 display: flex;
 align-items: center;
 gap: 12px;
 cursor: pointer;
 padding: 12px 16px;
 border-radius: 12px;
 transition: background-color 0.2s ease;
 min-height: 50px;
 
 &:hover {
   background-color: #f0f9ff;
 }
`;

const RadioInput = styled.input.attrs({ type: 'radio' })`
 width: 24px;
 height: 24px;
 border: 3px solid #00BBD3;
 border-radius: 50%;
 background-color: white;
 cursor: pointer;
 appearance: none;
 position: relative;
 transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
 transform: scale(1);
 flex-shrink: 0;
 
 &:hover {
   transform: scale(1.05);
   border-color: #009bb3;
 }
 
 &:checked {
   background-color: #00BBD3;
   transform: scale(1.1);
 }
 
 &:checked::after {
   content: '';
   position: absolute;
   top: 50%;
   left: 50%;
   transform: translate(-50%, -50%);
   width: 10px;
   height: 10px;
   background-color: white;
   border-radius: 50%;
   animation: checkPulse 0.3s ease;
 }
 
 &:active {
   transform: scale(0.95);
 }
`;

const RadioLabel = styled.label`
 font-size: 18px;
 color: #666666;
 cursor: pointer;
 font-weight: bold;
 font-family: 'Noto Sans Thai', sans-serif !important;
 line-height: 1.4;
 flex: 1;
`;

const SingleRadioGroup = styled.div`
 margin-bottom: 20px;
`;

const CheckboxSection = styled.div`
 margin-left: 24px;
 margin-top: 16px;
 max-height: ${props => props.$show ? '600px' : '0'};
 overflow: hidden;
 opacity: ${props => props.$show ? '1' : '0'};
 transform: translateY(${props => props.$show ? '0' : '-10px'});
 transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const CheckboxRow = styled.div`
 display: flex;
 align-items: center;
 gap: 12px;
 margin-bottom: 16px;
 transform: translateX(0);
 transition: transform 0.2s ease, opacity 0.2s ease, background-color 0.2s ease;
 padding: 12px 16px;
 border-radius: 12px;
 cursor: pointer;
 min-height: 50px;
 
 &:hover {
   transform: translateX(4px);
   background-color: #f0f9ff;
 }
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
 width: 24px;
 height: 24px;
 border: 3px solid #00BBD3;
 border-radius: 6px;
 background-color: white;
 cursor: pointer;
 appearance: none;
 transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
 transform: scale(1);
 flex-shrink: 0;
 
 &:hover {
   transform: scale(1.05);
   border-color: #009bb3;
 }
 
 &:checked {
   background-color: #00BBD3;
   background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='m13.854 3.646-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 9.293l6.646-6.647a.5.5 0 0 1 .708.708z'/%3e%3c/svg%3e");
   background-size: 14px;
   background-position: center;
   background-repeat: no-repeat;
   transform: scale(1.1);
 }
 
 &:active {
   transform: scale(0.95);
 }
`;

const CheckboxLabel = styled.label`
 font-size: 18px;
 color: #666666;
 cursor: pointer;
 font-weight: bold;
 font-family: 'Noto Sans Thai', sans-serif !important;
 line-height: 1.4;
 flex: 1;
`;

const DropdownContainer = styled.div`
 margin-left: 32px;
 margin-top: ${props => props.$show ? '8px' : '0'};
 margin-bottom: ${props => props.$show ? '8px' : '0'};
 max-height: ${props => props.$show ? '60px' : '0'};
 overflow: ${props => props.$show ? 'visible' : 'hidden'};
 opacity: ${props => props.$show ? '1' : '0'};
 transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

const Dropdown = styled.select`
 width: 200px;
 padding: 12px 16px;
 border: 2px solid #e1e5e9;
 border-radius: 12px;
 font-size: 16px;
 color: #666666;
 background-color: white;
 transition: border-color 0.3s ease;
 font-family: 'Noto Sans Thai', sans-serif !important;
 font-weight: bold;
 cursor: pointer;

 &:focus {
   outline: none;
   border-color: #00BBD3;
 }
`;

const SaveButton = styled.button`
 background-color: #00BBD3;
 color: white;
 border: none;
 border-radius: 30px;
 padding: 18px 32px;
 font-size: 18px;
 font-weight: bold;
 cursor: pointer;
 transition: all 0.3s ease;
 width: 100%;
 font-family: 'Noto Sans Thai', sans-serif !important;
 box-shadow: 0 9px 14px 0 rgba(0, 187, 211, 0.08);

 &:hover:not(:disabled) {
   background-color: #009bb3;
 }

 &:active:not(:disabled) {
   background-color: #008aa0;
 }

 &:disabled {
   opacity: 0.5;
   cursor: not-allowed;
 }
`;

const LogoutButton = styled.button`
 background-color: #e0e0e0;
 color: #666666;
 border: none;
 border-radius: 30px;
 padding: 18px 32px;
 font-size: 18px;
 font-weight: bold;
 cursor: pointer;
 transition: all 0.3s ease;
 width: 100%;
 font-family: 'Noto Sans Thai', sans-serif !important;
 box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.1);
 margin-top: 16px;

 &:hover {
   background-color: #d0d0d0;
 }

 &:active {
   background-color: #c0c0c0;
 }
`;

const CheckPulse = `
 @keyframes checkPulse {
   0% {
     transform: translate(-50%, -50%) scale(0);
     opacity: 0;
   }
   50% {
     transform: translate(-50%, -50%) scale(1.2);
     opacity: 0.8;
   }
   100% {
     transform: translate(-50%, -50%) scale(1);
     opacity: 1;
   }
 }
`;

const GlobalStyles = `
 ${CheckPulse}
`;

const ProfileEditPage = () => {
 const navigate = useNavigate();
 const { alertState, showAlert, closeAlert } = useCustomAlert();
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);

 const [userData, setUserData] = useState({
   idNumber: '',
   phone: '',
   firstName: '',
   lastName: '',
   gender: '',
   birthDate: '',
   profileImage: null
 });


 const [formData, setFormData] = useState({
   height: '',
   weight: '',
   waist: ''
 });

 const [diseaseData, setDiseaseData] = useState({
   consent: null, // null = ยังไม่เลือก, 'yes' = ยินยอม, 'no' = ไม่ยินยอม
   hasDisease: 'no',
   diseases: {
     diabetes: false,
     diabetesType: '',
     hypertension: false,
     kidneyDisease: false,
     hyperlipidemia: false,
     heartDisease: false,
     stroke: false,
     thyroid: false,
     emphysema: false
   }
 });


 // Load profile data on mount
 useEffect(() => {
   const loadProfile = async () => {
     try {
       setLoading(true);
       const response = await getProfile();
       console.log("📦 ProfileEdit - Full API response:", response);

       // Try multiple possible response structures
       let profile = null;
       if (response?.profile) {
         profile = response.profile;
       } else if (response?.data?.profile) {
         profile = response.data.profile;
       } else if (response?.data) {
         profile = response.data;
       } else if (response?.result) {
         profile = response.result;
       }

       console.log("👤 ProfileEdit - Parsed profile:", profile);

       // ดึงข้อมูล height, weight จาก MemberLabList API (เพราะ ProfileGet ส่งมาเป็น "0" หรือไม่ครบ)
       let labData = { height: '', weight: '', congenitaldisease: '', congenitaldisease_grant: '0' };
       try {
         const memberData = await getMemberLabData();
         if (memberData?.labData) {
           const ml = memberData.labData;
           labData.height = ml['height']?.[0]?.value || '';
           labData.weight = ml['weight']?.[0]?.value || '';
         }
       } catch (labError) {
         console.warn("⚠️ Could not fetch lab data:", labError);
       }


       // Get image path from backend
       let imagePath = profile?.image || profile?.profile_image || '';

       // Convert path to full Firebase URL
       let imageUrl = null;
       if (imagePath && imagePath !== '') {
         // If it's already a full URL, use it as is
         if (imagePath.startsWith('http')) {
           imageUrl = imagePath;
         } else {
           // Build full Firebase URL from path
           imageUrl = `https://firebasestorage.googleapis.com/v0/b/diet-z.appspot.com/o/${imagePath}`;
         }
       }


       if (profile) {
         // Set user data
         setUserData({
           idNumber: profile.nid || '',
           phone: profile.tel || '',
           firstName: profile.name || '',
           lastName: profile.lastname || '',
           gender: profile.gender === '1' ? 'ชาย' : profile.gender === '0' ? 'หญิง' : '',
           birthDate: profile.birthday || '',
           profileImage: imageUrl
         });

         // Set form data - ใช้ข้อมูลจาก MemberLabList ถ้ามี, ไม่งั้นใช้จาก ProfileGet
         setFormData({
           height: labData.height || (profile.height !== '0' ? profile.height : ''),
           weight: labData.weight || (profile.weight !== '0' ? profile.weight : ''),
           waist: profile.waistline !== '0' ? profile.waistline : ''
         });

         // Parse disease data
         // สำหรับ congenitaldisease (รายละเอียดโรค): ใช้จาก MemberLabList ก่อน (ถูกต้องกว่า)
         const finalCongenitaldisease = labData.congenitaldisease || profile.congenitaldisease || '';

         // ตรวจสอบว่า user มาจาก API โรงพยาบาล หรือไม่
         // ถ้า congenitaldisease เป็นค่าว่าง แสดงว่ายังไม่เคยเลือกยินยอม/ไม่ยินยอมเลย
         const isFromHospitalAPI = finalCongenitaldisease === '';

         // สำหรับ congenitaldisease_grant (ยินยอม/ไม่ยินยอม)
         const profileGrant = profile.congenitaldisease_grant;
         const labGrant = labData.congenitaldisease_grant;

         let finalConsentGrant = '';
         if (isFromHospitalAPI) {
           // ถ้ามาจาก API โรงพยาบาล และยังไม่มีข้อมูลโรค = ยังไม่เคยเลือก
           finalConsentGrant = '';
         } else {
           // ถ้าไม่ใช่ หรือ มีข้อมูลโรคแล้ว = user เคยเลือกแล้ว ให้ใช้ค่าจาก ProfileGet
           finalConsentGrant = profileGrant !== undefined && profileGrant !== null && profileGrant !== ''
             ? profileGrant
             : labGrant !== undefined && labGrant !== null && labGrant !== ''
               ? labGrant
               : '';
         }

        console.log("🔍 Profile congenitaldisease_grant:", profile.congenitaldisease_grant, "type:", typeof profile.congenitaldisease_grant);
        console.log("🔍 Profile congenitaldisease:", profile.congenitaldisease);
        console.log("🔬 Lab congenitaldisease_grant:", labData.congenitaldisease_grant, "type:", typeof labData.congenitaldisease_grant);
        console.log("🔬 Lab congenitaldisease:", labData.congenitaldisease);
        console.log("✅ Final consent grant:", finalConsentGrant, "type:", typeof finalConsentGrant);
         const consentGranted = finalConsentGrant === '1';

         if (consentGranted) {
           // ถ้ายินยอมแล้ว (consent grant = '1') - ใช้ข้อมูลจาก MemberLabList หรือ ProfileGet
           const diseaseText = (finalCongenitaldisease || '').toLowerCase().trim();
           const hasDiseases = diseaseText && diseaseText !== '';

           console.log("✅ User ยินยอม - setting consent: 'yes'");
           setDiseaseData(prev => ({
             ...prev,
             consent: 'yes',
             hasDisease: hasDiseases ? 'yes' : 'no',
             diseases: {
               diabetes: diseaseText.includes('เบาหวาน'),
               diabetesType: diseaseText.includes('เบาหวานชนิดที่ 1') ? 'type1'
                 : diseaseText.includes('เบาหวานชนิดที่ 2') ? 'type2'
                 : diseaseText.includes('เบาหวานขณะตั้งครรภ์') ? 'gestational'
                 : diseaseText.includes('เบาหวานชนิดอื่นๆ') ? 'other' : '',
               hypertension: diseaseText.includes('ความดันโลหิตสูง'),
               kidneyDisease: diseaseText.includes('โรคไตเรื้อรัง'),
               hyperlipidemia: diseaseText.includes('ไขมันในเลือดสูง'),
               heartDisease: diseaseText.includes('หัวใจ'),
               stroke: diseaseText.includes('หลอดเลือดสมอง'),
               thyroid: diseaseText.includes('ไทรอยด์'),
               emphysema: diseaseText.includes('ถุงลมโป่งพอง')
             }
           }));
         } else {
           // ถ้าไม่ยินยอม หรือยังไม่เคยเลือก
           // ต้องเป็น '0' อย่างชัดเจน (string zero) ถึงจะถือว่าเลือก "ไม่ยินยอม"
           // ถ้าเป็น undefined/null/'' (ยังไม่เคยเลือก) ให้เป็น null (ไม่ติ๊กอะไร)
           const hasExplicitNoConsent = finalConsentGrant === '0';
           console.log(hasExplicitNoConsent
             ? "❌ User เลือกไม่ยินยอมชัดเจน - setting consent: 'no'"
             : "⚪ User ยังไม่เคยเลือก - setting consent: null");
           setDiseaseData(prev => ({
             ...prev,
             consent: hasExplicitNoConsent ? 'no' : null
           }));
         }
       } else {
         console.error('❌ ProfileEdit - No profile data found in response');
       }
     } catch (error) {
       console.error('❌ ProfileEdit - Load profile error:', error);
       showAlert('ไม่สามารถโหลดข้อมูลโปรไฟล์ได้');
     } finally {
       setLoading(false);
     }
   };

   loadProfile();
 }, []);

 const handleUserDataChange = (field, value) => {
   setUserData(prev => ({
     ...prev,
     [field]: value
   }));
 };

 const handleInputChange = (field, value) => {
   setFormData(prev => ({
     ...prev,
     [field]: value
   }));
 };

 const handleDiseaseChange = (field, value) => {
   setDiseaseData(prev => ({
     ...prev,
     [field]: value
   }));
 };

 const handleDiseaseCheckbox = (disease, checked) => {
   setDiseaseData(prev => ({
     ...prev,
     diseases: {
       ...prev.diseases,
       [disease]: checked,
       ...(disease === 'diabetes' && !checked && { diabetesType: '' })
     }
   }));
 };

 const handleDiabetesType = (type) => {
   setDiseaseData(prev => ({
     ...prev,
     diseases: {
       ...prev.diseases,
       diabetesType: type
     }
   }));
 };

 const handleRadioGroupClick = (name, value) => {
   handleDiseaseChange(name, value);
 };

 const handleCheckboxRowClick = (disease) => {
   handleDiseaseCheckbox(disease, !diseaseData.diseases[disease]);
 };

 const handleSave = async () => {
   try {
     // Validate เบอร์มือถือ 10 หลัก
     if (userData.phone && userData.phone.length !== 10) {
       showAlert('กรุณากรอกเบอร์มือถือให้ครบ 10 หลัก');
       return;
     }

     setSaving(true);

     // Map disease data to backend format
     let congenitalDiseaseList = [];
     if (diseaseData.hasDisease === 'yes') {
       if (diseaseData.diseases.diabetes) {
         const diabetesTypeMap = {
           'type1': 'เบาหวานชนิดที่ 1',
           'type2': 'เบาหวานชนิดที่ 2',
           'gestational': 'เบาหวานขณะตั้งครรภ์',
           'other': 'เบาหวานชนิดอื่นๆ'
         };
         congenitalDiseaseList.push(diabetesTypeMap[diseaseData.diseases.diabetesType] || 'เบาหวาน');
       }
       if (diseaseData.diseases.hypertension) congenitalDiseaseList.push('ความดันโลหิตสูง');
       if (diseaseData.diseases.kidneyDisease) congenitalDiseaseList.push('โรคไตเรื้อรัง');
       if (diseaseData.diseases.hyperlipidemia) congenitalDiseaseList.push('ไขมันในเลือดสูง');
       if (diseaseData.diseases.heartDisease) congenitalDiseaseList.push('หัวใจ');
       if (diseaseData.diseases.stroke) congenitalDiseaseList.push('หลอดเลือดสมอง');
       if (diseaseData.diseases.thyroid) congenitalDiseaseList.push('ไทรอยด์');
       if (diseaseData.diseases.emphysema) congenitalDiseaseList.push('ถุงลมโป่งพอง');
     }

     // Extract path after /o/ from Firebase URL
     let imagePath = '';
     if (userData.profileImage) {
       const match = userData.profileImage.match(/\/o\/(.+)$/);
       imagePath = match ? match[1] : userData.profileImage;
     }

     // Prepare update payload
     const updateData = {
       tel: userData.phone,
       name: userData.firstName,
       lastname: userData.lastName,
       gender: userData.gender === 'ชาย' ? '1' : '0',
       birthday: userData.birthDate,
       height: formData.height,
       weight: formData.weight,
       waistline: formData.waist,
       congenitaldisease_grant: diseaseData.consent === 'yes' ? '1' : diseaseData.consent === 'no' ? '0' : '',
       congenitaldisease: congenitalDiseaseList.join(', '),
       image: imagePath  // Send only path after /o/
     };

     console.log('Updating profile:', updateData);

     const response = await updateProfile(updateData);
     console.log('Update response:', response);

     if (response.status === 'success' || response.result) {
       showAlert('บันทึกข้อมูลเรียบร้อยแล้ว');
       navigate('/dashboard');
     } else {
       alert(response.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล');
     }
   } catch (error) {
     console.error('Save profile error:', error);
     showAlert('เกิดข้อผิดพลาดในการบันทึกข้อมูล กรุณาลองอีกครั้ง');
   } finally {
     setSaving(false);
   }
 };

 const handleProfileImageChange = (imageUrl) => {
   setUserData(prev => ({
     ...prev,
     profileImage: imageUrl
   }));
 };


 const handleLogout = () => {
   if (window.confirm('คุณต้องการออกจากระบบหรือไม่?')) {
     // Clear all localStorage data
     localStorage.clear();

     // Hard refresh to clear all cached data
     window.location.href = '/login';
   }
 };

 if (loading) {
   return (
     <>
       <style>{GlobalStyles}</style>
       <ProfileContainer>
         <MainHeader />
         <Content>
           <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
             กำลังโหลดข้อมูล...
           </div>
         </Content>
       </ProfileContainer>
     </>
   );
 }

 return (
   <>
     <style>{GlobalStyles}</style>
     {alertState.isOpen && (
       <CustomAlert message={alertState.message} onClose={closeAlert} />
     )}
     <ProfileContainer>
       <MainHeader />

       <Content>
         <ProfileSection>
           <ProfileImageUpload
             imageUrl={userData.profileImage}
             onImageChange={handleProfileImageChange}
             userId={userData.idNumber || 'temp'}
             disabled={loading || saving}
           />
         </ProfileSection>

         <SectionTitle>ข้อมูลส่วนตัว</SectionTitle>
         
         <InfoGrid>
           <InfoRow>
             <InfoLabel>เบอร์มือถือ</InfoLabel>
             <InfoValue
               value={userData.phone}
               onChange={(e) => {
                 const value = e.target.value.replace(/\D/g, ''); // รับเฉพาะตัวเลข
                 if (value.length <= 10) {
                   handleUserDataChange('phone', value);
                 }
               }}
               maxLength={10}
             />
           </InfoRow>
           <InfoRow>
             <InfoLabel>ชื่อ</InfoLabel>
             <InfoValue 
               value={userData.firstName}
               onChange={(e) => handleUserDataChange('firstName', e.target.value)}
             />
           </InfoRow>
           <InfoRow>
             <InfoLabel>นามสกุล</InfoLabel>
             <InfoValue 
               value={userData.lastName}
               onChange={(e) => handleUserDataChange('lastName', e.target.value)}
             />
           </InfoRow>
           <InfoRow>
             <InfoLabel>เพศ</InfoLabel>
             <InfoValue 
               value={userData.gender}
               onChange={(e) => handleUserDataChange('gender', e.target.value)}
             />
           </InfoRow>
           <InfoRow>
             <InfoLabel>วันเดือนปีเกิด</InfoLabel>
             <InfoValue 
               value={userData.birthDate}
               onChange={(e) => handleUserDataChange('birthDate', e.target.value)}
             />
           </InfoRow>
         </InfoGrid>

         <FormSection>
           <FormRow>
             <FormLabel>ส่วนสูง</FormLabel>
             <FormInputContainer>
               <SmallInput
                 type="number"
                 value={formData.height}
                 onChange={(e) => handleInputChange('height', e.target.value)}
               />
               <UnitLabel>เซนติเมตร</UnitLabel>
             </FormInputContainer>
           </FormRow>

           <FormRow>
             <FormLabel>น้ำหนัก</FormLabel>
             <FormInputContainer>
               <SmallInput
                 type="number"
                 value={formData.weight}
                 onChange={(e) => handleInputChange('weight', e.target.value)}
               />
               <UnitLabel>กิโลกรัม</UnitLabel>
             </FormInputContainer>
           </FormRow>

           <FormRow>
             <FormLabel>วัดรอบเอว</FormLabel>
             <FormInputContainer>
               <SmallInput
                 type="number"
                 value={formData.waist}
                 onChange={(e) => handleInputChange('waist', e.target.value)}
               />
               <UnitLabel>(นิ้ว)</UnitLabel>
             </FormInputContainer>
           </FormRow>
         </FormSection>

         <DiseaseSection>
           <DiseaseTitle>โรคประจำตัว (ถ้ามี)</DiseaseTitle>
           <DiseaseDescription>
             เราเก็บข้อมูลโรคประจำตัวของคุณเพื่อใช้ในการจัดทำคำแนะนำที่เหมาะสมกับการดูแลสุขภาพของคุณ หากคุณประสงค์จะให้ข้อมูลกรุณากด ยินยอม
           </DiseaseDescription>

           <RadioRow>
             <RadioGroup onClick={() => handleRadioGroupClick('consent', 'yes')}>
               <RadioInput
                 name="consent"
                 value="yes"
                 checked={diseaseData.consent === 'yes'}
                 onChange={(e) => handleDiseaseChange('consent', e.target.value)}
               />
               <RadioLabel>ยินยอม</RadioLabel>
             </RadioGroup>
             <RadioGroup onClick={() => handleRadioGroupClick('consent', 'no')}>
               <RadioInput
                 name="consent"
                 value="no"
                 checked={diseaseData.consent === 'no'}
                 onChange={(e) => handleDiseaseChange('consent', e.target.value)}
               />
               <RadioLabel>ไม่ยินยอม</RadioLabel>
             </RadioGroup>
           </RadioRow>

           {diseaseData.consent === 'yes' && (
             <>
               <SingleRadioGroup>
                 <RadioGroup onClick={() => handleRadioGroupClick('hasDisease', 'no')}>
                   <RadioInput
                     name="hasDisease"
                     value="no"
                     checked={diseaseData.hasDisease === 'no'}
                     onChange={(e) => handleDiseaseChange('hasDisease', e.target.value)}
                   />
                   <RadioLabel>ไม่มีโรคประจำตัว</RadioLabel>
                 </RadioGroup>
               </SingleRadioGroup>

               <SingleRadioGroup>
                 <RadioGroup onClick={() => handleRadioGroupClick('hasDisease', 'yes')}>
                   <RadioInput
                     name="hasDisease"
                     value="yes"
                     checked={diseaseData.hasDisease === 'yes'}
                     onChange={(e) => handleDiseaseChange('hasDisease', e.target.value)}
                   />
                   <RadioLabel>มีโรคประจำตัว ระบุได้หลายโรค</RadioLabel>
                 </RadioGroup>

                 <CheckboxSection $show={diseaseData.hasDisease === 'yes'}>
                   <CheckboxRow onClick={() => handleCheckboxRowClick('diabetes')}>
                     <Checkbox
                       checked={diseaseData.diseases.diabetes}
                       onChange={(e) => handleDiseaseCheckbox('diabetes', e.target.checked)}
                     />
                     <CheckboxLabel>เบาหวาน ชนิด</CheckboxLabel>
                   </CheckboxRow>
                   <DropdownContainer $show={diseaseData.diseases.diabetes}>
                     <Dropdown
                       value={diseaseData.diseases.diabetesType}
                       onChange={(e) => handleDiabetesType(e.target.value)}
                     >
                       <option value="">เลือกประเภท</option>
                       <option value="type1">เบาหวานชนิดที่ 1</option>
                       <option value="type2">เบาหวานชนิดที่ 2</option>
                       <option value="gestational">เบาหวานขณะตั้งครรภ์</option>
                       <option value="other">เบาหวานชนิดอื่นๆ</option>
                     </Dropdown>
                   </DropdownContainer>

                   <CheckboxRow onClick={() => handleCheckboxRowClick('hypertension')}>
                     <Checkbox
                       checked={diseaseData.diseases.hypertension}
                       onChange={(e) => handleDiseaseCheckbox('hypertension', e.target.checked)}
                     />
                     <CheckboxLabel>ความดันโลหิตสูง</CheckboxLabel>
                   </CheckboxRow>

                   <CheckboxRow onClick={() => handleCheckboxRowClick('kidneyDisease')}>
                     <Checkbox
                       checked={diseaseData.diseases.kidneyDisease}
                       onChange={(e) => handleDiseaseCheckbox('kidneyDisease', e.target.checked)}
                     />
                     <CheckboxLabel>โรคไตเรื้อรัง</CheckboxLabel>
                   </CheckboxRow>

                   <CheckboxRow onClick={() => handleCheckboxRowClick('hyperlipidemia')}>
                     <Checkbox
                       checked={diseaseData.diseases.hyperlipidemia}
                       onChange={(e) => handleDiseaseCheckbox('hyperlipidemia', e.target.checked)}
                     />
                     <CheckboxLabel>ไขมันในเลือดสูง</CheckboxLabel>
                   </CheckboxRow>

                   <CheckboxRow onClick={() => handleCheckboxRowClick('heartDisease')}>
                     <Checkbox
                       checked={diseaseData.diseases.heartDisease}
                       onChange={(e) => handleDiseaseCheckbox('heartDisease', e.target.checked)}
                     />
                     <CheckboxLabel>หัวใจ</CheckboxLabel>
                   </CheckboxRow>

                   <CheckboxRow onClick={() => handleCheckboxRowClick('stroke')}>
                     <Checkbox
                       checked={diseaseData.diseases.stroke}
                       onChange={(e) => handleDiseaseCheckbox('stroke', e.target.checked)}
                     />
                     <CheckboxLabel>หลอดเลือดสมอง</CheckboxLabel>
                   </CheckboxRow>

                   <CheckboxRow onClick={() => handleCheckboxRowClick('thyroid')}>
                     <Checkbox
                       checked={diseaseData.diseases.thyroid}
                       onChange={(e) => handleDiseaseCheckbox('thyroid', e.target.checked)}
                     />
                     <CheckboxLabel>ไทรอยด์</CheckboxLabel>
                   </CheckboxRow>

                   <CheckboxRow onClick={() => handleCheckboxRowClick('emphysema')}>
                     <Checkbox
                       checked={diseaseData.diseases.emphysema}
                       onChange={(e) => handleDiseaseCheckbox('emphysema', e.target.checked)}
                     />
                     <CheckboxLabel>ถุงลมโป่งพอง</CheckboxLabel>
                   </CheckboxRow>
                 </CheckboxSection>
               </SingleRadioGroup>
             </>
           )}
         </DiseaseSection>

         <SaveButton onClick={handleSave} disabled={saving || loading}>
           {saving ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}
         </SaveButton>

         <LogoutButton onClick={handleLogout}>
           Logout
         </LogoutButton>
       </Content>
     </ProfileContainer>
   </>
 );
};

export default ProfileEditPage;